import type { FunctionComponent } from "react";
import { useLayoutEffect, useCallback, useContext, useMemo, useState, useRef } from "react";

import { QuickStakes } from "@ppb/the-wall-web";
import { BetslipSection } from "../Betslip.types";
import ConnectedNotifier from "../Notifier";
import { Notifier } from "../Notifier/Notifier.web";
import { KeyboardContext } from "../Keyboard/KeyboardContext";

import { ComponentProps } from "./props";
import styles from "./SportsbookPlace.web.css";
import { EmptyBetslip } from "../EmptyBetslip";
import { FooterCustomKeyboard } from "../Keyboard/FooterCustomKeyboard.web";
import { SportsbookPlacePlaceholder } from "./SportsbookPlacePlaceholder.web";
import { SportsbookPlacePanel } from "./snowflakes/SportsbookPlacePanel/BetslipView/SportsbookPlacePanel.web";

export const SportsbookPlace: FunctionComponent<ComponentProps> = ({
  config,
  betBuilderIds,
  multiBetBuilderIds,
  oneLineMultipleId,
  boostedCombinationIds,
  failedCombinationGroups,
  failedCombinationGroupIds,
  totalReturns,
  totalOriginalReturns,
  hasPlaceError,
  isPlaceDisabled,
  isSummaryDisabled,
  isFreeBetsSelected,
  isOddsBoosted,
  isEligibleToBonus,
  hasSingles,
  hasOnlyOneSingle,
  isQuickBetslipActive = false,
  hasMultiBetBuilder,
  hasCastBets,
  hasBetBuilders,
  hasPriceBoost,
  hasLotteries,
  shouldFocusMultiple,
  shouldFocusBetBuilder,
  shouldFocusCastBet,
  focusedCard,
  firstCombinationId,
  placeBtnLabel,
  placeBtnSecondaryLabel,
  placeBtnLoadingLabel,
  reversePlaceBtnLabels,
  balanceAfterBet,
  isLoggedIn,
  isBetConfirmationStepEnabled,
  isDepositRequired,
  requiredDepositValue,
  isPanelDisabled,
  isOddsMovementOn,
  showAcceptOddsMovementAlert,
  oddsMovementLabels,
  isCollapsed,
  i18n,
  termsUrl,
  hasOneLineMultiple,
  hasMultipleLinesMultiples,
  shouldDisplayPlaceholder,
  marketSelections,
  withAllPebbleEnabled,
  isUpsellSuggestionsEnabled,
  isSinglesFirst,
  freeBetsAlertMessage,
  quickStakes,
  separator,
  currencySymbol,
  multiBetBuilderTitle,
  oneLineMultipleTitle,
  betBuilderTitles,
  dispatchPlacement,
  dispatchRemoveAll,
  dispatchSportsbookBonusToggle,
  requestLogin,
  dispatchConfirmBet,
  dispatchDepositRedirect,
  dispatchTabSwitch,
  dispatchLoginToPlaceBetAction,
  dispatchFreeBetsRemoveAction,
  dispatchIncrementPress,
  dispatchOddsMovementChange,
}) => {
  const { isDesktopLayout } = config;

  const {
    focusedKeyboardControls: { focusedCombinationId },
    setFocusedKeyboardControls,
  } = useContext(KeyboardContext);
  const ref = useRef<HTMLDivElement | null>(null);
  const [placeholderHeight, setPlaceholderHeight] = useState<number>(0);
  useLayoutEffect(() => {
    if (ref.current?.offsetHeight) {
      setPlaceholderHeight(ref?.current?.offsetHeight);
    }
  }, [ref.current?.offsetHeight]);

  /**
   * It's necessary to clear the focusedInputId whenever the betslip changes in its composition, so that its value
   * could be assigned in the BetControls component if it's the instance that should receive the automatic focus.
   * However, the focusedInputId shouldn't be clean when focusedCard is "singles" or if the focused input is the first
   * combination.
   */
  const clearFocusedInputId = useCallback(() => {
    if (focusedCard !== BetslipSection.singles || firstCombinationId !== focusedCombinationId) {
      setFocusedKeyboardControls((prev) => ({
        ...prev,
        focusedInputId: null,
      }));
    }
    // We do not need to listen for all the dependencies of the effect,
    // adding focusedInputId props to this effect produces undesired outputs and running at the wrong times
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedCard, firstCombinationId, setFocusedKeyboardControls]);

  useLayoutEffect(() => {
    clearFocusedInputId();
  }, [clearFocusedInputId]);

  const handlePlaceClick = useCallback(() => {
    if (!isLoggedIn) {
      dispatchLoginToPlaceBetAction();

      requestLogin();

      return;
    }

    if (isBetConfirmationStepEnabled) {
      dispatchConfirmBet();

      return;
    }

    if (isDepositRequired) {
      dispatchDepositRedirect(requiredDepositValue);

      return;
    }

    dispatchPlacement();
  }, [
    dispatchConfirmBet,
    dispatchDepositRedirect,
    requestLogin,
    dispatchLoginToPlaceBetAction,
    dispatchPlacement,
    isBetConfirmationStepEnabled,
    isDepositRequired,
    isLoggedIn,
    requiredDepositValue,
  ]);

  const handleFreeBetsChange = useCallback(() => {
    dispatchSportsbookBonusToggle(isFreeBetsSelected);
  }, [dispatchSportsbookBonusToggle, isFreeBetsSelected]);

  const notifications = useMemo(
    () => (
      <ConnectedNotifier
        className={styles.notificationsListContainer}
        component={Notifier}
        isBetslipCollapsed={isCollapsed}
      />
    ),
    [isCollapsed],
  );

  const onQuickStakePress = useCallback(
    (stake: number) => {
      if (focusedCombinationId) {
        dispatchIncrementPress(focusedCombinationId, stake, currencySymbol);
      }
    },
    [focusedCombinationId, dispatchIncrementPress, currencySymbol],
  );

  const quickStakesComponent = useMemo(
    () => (
      <div className={styles.quickStakes}>
        <QuickStakes quickStakes={quickStakes} onTouch={onQuickStakePress} isDisabled={isPanelDisabled} />
      </div>
    ),
    [quickStakes, isPanelDisabled, onQuickStakePress],
  );

  const footerPrefix = useMemo(
    () => (
      <FooterCustomKeyboard
        className={styles.keyboardContainer}
        prefix={quickStakesComponent}
        separator={separator}
        isDisabled={isPanelDisabled || false}
        shouldScrollIntoView={!isCollapsed}
        isDesktop={isDesktopLayout}
      />
    ),
    [isCollapsed, isPanelDisabled, quickStakesComponent, separator, isDesktopLayout],
  );

  if (shouldDisplayPlaceholder) {
    if (isDesktopLayout) {
      return <EmptyBetslip />;
    }

    return <SportsbookPlacePlaceholder height={placeholderHeight} />;
  }

  return (
    <div ref={ref}>
      <SportsbookPlacePanel
        hasFreeBets={isEligibleToBonus}
        isFreeBetsSelected={isFreeBetsSelected}
        freeBetsAlertMessage={freeBetsAlertMessage}
        onFreeBetsRemovePress={dispatchFreeBetsRemoveAction}
        isOddsBoosted={isOddsBoosted}
        totalReturns={totalReturns}
        totalOriginalReturns={totalOriginalReturns}
        hasPlaceError={hasPlaceError || isDepositRequired || !isLoggedIn}
        isPlaceDisabled={isPlaceDisabled}
        isSummaryDisabled={isSummaryDisabled}
        notifications={notifications}
        footerPrefix={footerPrefix}
        placeBtnLabel={placeBtnLabel}
        placeBtnSecondaryLabel={placeBtnSecondaryLabel}
        placeBtnLoadingLabel={placeBtnLoadingLabel}
        reversePlaceBtnLabels={reversePlaceBtnLabels}
        isDesktop={isDesktopLayout}
        balanceAfterBet={balanceAfterBet}
        i18n={i18n}
        termsUrl={termsUrl}
        isPanelDisabled={isPanelDisabled}
        isOddsMovementOn={isOddsMovementOn}
        showAcceptOddsMovementAlert={showAcceptOddsMovementAlert}
        oddsMovementLabels={oddsMovementLabels}
        onPlaceClick={handlePlaceClick}
        onRemoveAllClick={dispatchRemoveAll}
        onFreeBetsChange={handleFreeBetsChange}
        onOddsMovementPreferencesChange={dispatchOddsMovementChange}
        onTabSwitch={dispatchTabSwitch}
        isLoggedIn={isLoggedIn}
        hasSingles={hasSingles}
        hasLotteries={hasLotteries}
        hasOnlyOneSingle={hasOnlyOneSingle}
        isQuickBetslipActive={isQuickBetslipActive}
        hasOneLineMultiple={hasOneLineMultiple}
        hasMultipleLinesMultiples={hasMultipleLinesMultiples}
        hasMultiBetBuilder={hasMultiBetBuilder}
        hasCastBets={hasCastBets}
        shouldFocusMultiple={shouldFocusMultiple}
        shouldFocusBetBuilder={shouldFocusBetBuilder}
        shouldFocusCastBet={shouldFocusCastBet}
        hasPriceBoost={hasPriceBoost}
        hasBetBuilders={hasBetBuilders}
        betBuilderIds={betBuilderIds}
        multiBetBuilderIds={multiBetBuilderIds}
        oneLineMultipleId={oneLineMultipleId}
        failedCombinationGroups={failedCombinationGroups}
        boostedCombinationIds={boostedCombinationIds}
        failedCombinationGroupIds={failedCombinationGroupIds}
        marketSelections={marketSelections}
        withAllPebbleEnabled={withAllPebbleEnabled}
        isUpsellSuggestionsEnabled={isUpsellSuggestionsEnabled}
        isSinglesFirst={isSinglesFirst}
        multiBetBuilderTitle={multiBetBuilderTitle}
        betBuilderTitles={betBuilderTitles}
        oneLineMultipleTitle={oneLineMultipleTitle}
      />
    </div>
  );
};
